import { Button, Modal, Tag, Typography, theme } from "antd";
import { AuditOutlined, FileTextOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { PRESETS, type Preset } from "./presets";

const { Text, Title } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (preset: Preset) => void;
  /** True when the process already has steps, so the copy warns rather than invites. */
  hasStages: boolean;
};

/**
 * Pick a starting shape.
 *
 * Each card previews the actual spine it will create, because the point is to
 * teach the model, not to offer a name: seeing "fill in → sign off → fill in"
 * as a row of chips is what makes the two kinds of step, and the fact that data
 * entry can come *after* an approval, obvious at a glance.
 */
const PresetPicker = ({ open, onClose, onPick, hasStages }: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={Math.min(880, window.innerWidth - 64)}
      title={t("Start from a shape")}
    >
      <Text type="secondary">
        {hasStages
          ? t(
              "This adds the preset's steps to the end of the process you already have. Nothing is removed, and you can undo it."
            )
          : t(
              "Pick the shape closest to your process — you can rename, reorder and add steps afterwards. Nothing is saved until you press Save."
            )}
      </Text>

      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          marginTop: 16,
        }}
      >
        {PRESETS.map((preset) => (
          <div
            key={preset.id}
            role="button"
            tabIndex={0}
            onClick={() => {
              onPick(preset);
              onClose();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onPick(preset);
                onClose();
              }
            }}
            style={{
              padding: 14,
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorderSecondary}`,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              transition: "border-color .15s, box-shadow .15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = token.colorPrimary;
              e.currentTarget.style.boxShadow = token.boxShadowTertiary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = token.colorBorderSecondary;
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              {t(preset.name)}
            </Title>
            <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>
              {t(preset.description)}
            </Text>

            {/* The spine it will build, so the shape is visible before it is chosen. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {preset.stages.map((stage, i) => {
                const entry = stage.kind === "DataEntry";
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "3px 8px",
                      borderRadius: token.borderRadius,
                      background: token.colorFillQuaternary,
                      borderLeft: `3px solid ${
                        entry ? token.colorPrimary : token.purple6
                      }`,
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: token.colorTextTertiary, display: "flex" }}>
                      {entry ? <FileTextOutlined /> : <AuditOutlined />}
                    </span>
                    <span style={{ flex: 1 }}>{t(stage.name)}</span>
                    {stage.hint && (
                      <Tag
                        bordered={false}
                        style={{ marginInlineEnd: 0, fontSize: 10 }}
                      >
                        {t(stage.hint)}
                      </Tag>
                    )}
                  </div>
                );
              })}
            </div>

            <Button type="primary" size="small" block>
              {t("Use this shape")}
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default PresetPicker;
